import { Immutable } from '@zervant/reactcomponents'
import { DocumentTypes } from 'client/Interfaces/Draft/DraftInterface'
import { CompanyV1Interface } from 'client/Interfaces/Draft/v1/DraftInterface'
import { SchemaItemInterface } from 'client/Interfaces/Schema/SchemaItemInterface'
import { FieldInterface } from 'client/Interfaces/Settings/FieldInterface'
import { ModelInterface } from 'client/Interfaces/Settings/ModelInterface'
import { createSelector } from 'reselect'

import { SchemaInterface, SettingsInterface, SettingsSchemasType } from '../Interfaces/Settings/SettingsInterface'
import { StateInterface } from '../Interfaces/StateInterface'
import { transformDateFormatForMoment, transformDateTimeFormat } from '../Other/functions/dateFunctions'
import { ReducerBuilder } from './ReducerBuilder'

export const DEFAULT_UNITS = {
  fi: ['cm', 'm', 'km', 'kg', 'h', 'kpl'],
  sv: ['cm', 'm', 'km', 'kg', 'h', 'st'],
  de: ['cm', 'm', 'km', 'kg', 'h', 'Stk.'],
  fr: ['cm', 'm', 'km', 'kg', 'h', 'pce'],
  en: ['cm', 'm', 'km', 'kg', 'h', 'pcs'],
  nl: ['cm', 'm', 'km', 'kg', 'h', 'Stk.'],
}

const INITIAL_STATE = null

type StateType = SettingsInterface | null

function getSettings(state: StateType, payload: SettingsInterface): StateType {
  return updateSettings(payload)
}

function updateValues(state: StateType, payload: SettingsInterface): StateType {
  return updateSettings(payload)
}

function initializeSettings(state: StateType, payload: SettingsInterface): StateType {
  return updateSettings(payload)
}

function updateSettings(payload: SettingsInterface) {
  return { ...Immutable.copy(payload), isOneProductUser: hasSettingsViewAvailable(payload, 'reactSubscription') }
}

const reducer = ReducerBuilder.create<StateType, SettingsInterface>(INITIAL_STATE)
  .mapAction('GET_SETTINGS', getSettings)
  .mapAction('UPDATE_VALUES', updateValues)
  .mapAction('INITIALIZE_SETTINGS', initializeSettings)
  .build()

export default reducer
export enum moduleNames {
  work = 'work',
  reactInvoiceList = 'reactInvoiceList',
  recurringProfiles = 'recurringProfiles',
  reactDashboard = 'reactDashboard',
  xRechnung = 'xRechnung',
  banking = 'banking',
  agerasInvoiceFinancing = 'agerasInvoiceFinancing',
  agerasBusinessLoan = 'agerasBusinessLoan',
  sveaInvoiceFinancing = 'sveaInvoiceFinancing',
  tinkPayment = 'tinkPayment',
  agerasAccounting = 'agerasAccounting',
  agerasDirectAccounting = 'agerasDirectAccounting',
}
// Check if module is available in settings, not checking whether hidden or visible
export const hasModuleSettings = (settings: SettingsInterface, module: moduleNames): boolean => {
  return settings.modules?.some((mod) => mod?.name === module)
}

// Check if module is available in settings
export const hasModuleAvailable = (settings: SettingsInterface, module: moduleNames): boolean => {
  return settings?.modules?.some((mod) => mod?.name === module && !mod?.hidden)
}

// Check if view is available under module -> settings
export const hasSettingsViewAvailable = (
  settings: SettingsInterface,
  view: 'payNow' | 'reactSubscription',
): boolean => {
  return (
    settings.modules &&
    settings.modules.find((module) => module.name === 'settings').views?.some((v) => v?.view === view && !v?.hidden)
  )
}

// selectors
export const settingsSelector = (state: StateInterface): SettingsInterface => state.settings
export const tradeNameV1Selector = (
  state: StateInterface,
  tradeNameId?: CompanyV1Interface['tradeName']['_id'],
): CompanyV1Interface => {
  const allTradeNames = settingsSelector(state).values.company.tradeNames
  let tradeName = allTradeNames[0]
  if (tradeNameId) {
    tradeName = allTradeNames.find((tn) => tn._id === tradeNameId) || allTradeNames[0]
  }
  return { tradeName } as unknown as CompanyV1Interface
}
export const longDateSelector = createSelector(settingsSelector, (settings) =>
  transformDateFormatForMoment(settings?.values?.regional?.longDate),
)

export const longDateTimeSelector = createSelector<StateInterface, SettingsInterface, string>(
  settingsSelector,
  (settings) =>
    transformDateTimeFormat(settings?.values?.regional?.longDate, settings?.values?.regional?.longTime || 'h:i'),
)

export const schemasSelector = createSelector<StateInterface, SettingsInterface, SettingsInterface['schemas']>(
  [settingsSelector],
  (settings) => settings.schemas,
)

export const schemaSelector = (state: StateInterface, schema: SettingsSchemasType): SchemaInterface => {
  return schemasSelector(state)[schema]
}

export const schemaFieldsToMapSelector = createSelector<
  StateInterface,
  SettingsSchemasType,
  SchemaInterface,
  Record<SchemaItemInterface['name'], SchemaItemInterface>
>([schemaSelector], (model) =>
  model.fields.reduce(
    (acc, field) => {
      return { ...acc, [field.name]: field }
    },
    {} as Record<SchemaItemInterface['name'], SchemaItemInterface>,
  ),
)

export const modelsSelector = createSelector<StateInterface, SettingsInterface, SettingsInterface['models']>(
  [settingsSelector],
  (settings) => settings.models,
)

export const modelSelector = (state: StateInterface, model: DocumentTypes): ModelInterface => {
  return modelsSelector(state).find(({ type }) => type === model)
}

export const modelFieldsToMapSelector = createSelector<
  StateInterface,
  SettingsInterface['models'][0]['type'],
  ModelInterface,
  Record<FieldInterface['name'], FieldInterface>
>([modelSelector], (model) =>
  model.fields.reduce(
    (acc, field) => {
      return { ...acc, [field.name]: field }
    },
    {} as Record<FieldInterface['name'], FieldInterface>,
  ),
)
